import type { Security } from '../Security';
import { SubProductTypeEnum } from '../types';
import type {
  OTCFixedRateLoanProductTemplate,
  OTCFixedRateLoanSecurity,
  OTCFloatingRateLoanProductTemplate,
  OTCFloatingRateLoanSecurity,
  OTCForwardProductTemplate,
  OTCForwardSecurity,
  OTCOptionProductTemplate,
  OTCOptionSecurity,
  OTCRepoProductTemplate,
  OTCRepoSecurity,
} from './otcSecurityServerTypes';

// ---------------------------------------------------------------------------
// Type guards
// ---------------------------------------------------------------------------

// When Deals are created, related securities are created in SecMaster with a ProductDetails object.

export const isOTCForwardSecurity = (security: Security | undefined): security is OTCForwardSecurity => {
  return security?.SubProductType === SubProductTypeEnum.OTCForward && !!security.ProductDetails;
};
export const isFixedRateLoanSecurity = (security: Security | undefined): security is OTCFixedRateLoanSecurity => {
  return security?.SubProductType === SubProductTypeEnum.OTCFixedRateLoan && !!security.ProductDetails;
};
export const isFloatingRateLoanSecurity = (security: Security | undefined): security is OTCFloatingRateLoanSecurity => {
  return security?.SubProductType === SubProductTypeEnum.OTCFloatingRateLoan && !!security.ProductDetails;
};
export const isOTCOptionSecurity = (security: Security | undefined): security is OTCOptionSecurity => {
  return security?.SubProductType === SubProductTypeEnum.OTCVanillaOption && !!security.ProductDetails;
};

const hasTemplateSecurity = (security: Security): boolean => {
  return !!(security.ProductDetails as { TemplateSecurity?: unknown }).TemplateSecurity;
};

export const isOTCForwardProductTemplate = (security: Security | undefined): security is OTCForwardProductTemplate => {
  return isOTCForwardSecurity(security) && !hasTemplateSecurity(security);
};
export const isInstantiatedOTCForwardSecurity = (security: Security | undefined): security is OTCForwardSecurity => {
  return isOTCForwardSecurity(security) && hasTemplateSecurity(security);
};
export const isFixedRateLoanProductTemplate = (
  security: Security | undefined
): security is OTCFixedRateLoanProductTemplate => {
  return isFixedRateLoanSecurity(security) && !hasTemplateSecurity(security);
};
export const isInstantiatedFixedRateLoanSecurity = (
  security: Security | undefined
): security is OTCFixedRateLoanSecurity => {
  return isFixedRateLoanSecurity(security) && hasTemplateSecurity(security);
};
export const isFloatingRateLoanProductTemplate = (
  security: Security | undefined
): security is OTCFloatingRateLoanProductTemplate => {
  return isFloatingRateLoanSecurity(security) && !hasTemplateSecurity(security);
};
export const isInstantiatedFloatingRateLoanSecurity = (
  security: Security | undefined
): security is OTCFloatingRateLoanSecurity => {
  return isFloatingRateLoanSecurity(security) && hasTemplateSecurity(security);
};
export const isOTCOptionProductTemplate = (security: Security | undefined): security is OTCOptionProductTemplate => {
  return isOTCOptionSecurity(security) && !hasTemplateSecurity(security);
};
export const isInstantiatedOTCOptionSecurity = (security: Security | undefined): security is OTCOptionSecurity => {
  return isOTCOptionSecurity(security) && hasTemplateSecurity(security);
};
export const isOTCRepoSecurity = (security: Security | undefined): security is OTCRepoSecurity => {
  return security?.SubProductType === SubProductTypeEnum.OTCRepo && !!security.ProductDetails;
};
export const isOTCRepoProductTemplate = (security: Security | undefined): security is OTCRepoProductTemplate => {
  return isOTCRepoSecurity(security) && !hasTemplateSecurity(security);
};
export const isInstantiatedOTCRepoSecurity = (security: Security | undefined): security is OTCRepoSecurity => {
  return isOTCRepoSecurity(security) && hasTemplateSecurity(security);
};
